import { Component, OnInit, OnDestroy } from '@angular/core';
import { BackgroundUploadService, UploadTask, UploadNotification } from '../../services/background-upload.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-global-upload-status',
    templateUrl: './global-upload-status.component.html',
    styleUrls: ['./global-upload-status.component.css'],
    standalone: false
})
export class GlobalUploadStatusComponent implements OnInit, OnDestroy {
  uploadTasks: UploadTask[] = [];
  showUploadStatus = false;
  showNotification = true;
  private subscriptions: Subscription[] = [];
  private notificationTimeout: any;

  constructor(private backgroundUploadService: BackgroundUploadService) { }

  ngOnInit(): void {
    this.subscriptions.push(
      this.backgroundUploadService.uploadTasks$.subscribe(tasks => {
        this.uploadTasks = tasks;
        
        // Show notification when new uploads start
        if (this.hasActiveUploads()) {
          this.showNotification = true;
          this.startNotificationTimer();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }
  }

  /**
   * Start notification timer to hide after 5 seconds
   */
  private startNotificationTimer(): void {
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }
    
    this.notificationTimeout = setTimeout(() => {
      this.showNotification = false;
    }, 5000); // Hide after 5 seconds
  }


  getActiveUploadsCount(): number {
    return this.uploadTasks.filter(task => 
      task.status === 'pending' || task.status === 'uploading' || task.status === 'processing'
    ).length;
  }

  getCompletedUploadsCount(): number {
    return this.uploadTasks.filter(task => task.status === 'completed').length;
  }

  getFailedUploadsCount(): number {
    return this.uploadTasks.filter(task => task.status === 'failed').length;
  }

  hasActiveUploads(): boolean {
    return this.getActiveUploadsCount() > 0;
  }

  hasCompletedUploads(): boolean {
    return this.getCompletedUploadsCount() > 0;
  }

  hasFailedUploads(): boolean {
    return this.getFailedUploadsCount() > 0;
  }

  /**
   * Format file size
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Check if task is in progress
   */
  isTaskInProgress(task: UploadTask): boolean {
    return task.status === 'uploading' || task.status === 'processing';
  }

  /**
   * Get progress bar class based on task status
   */
  getProgressBarClass(task: UploadTask): string {
    return task.status === 'uploading' ? 'bg-primary' : 'bg-warning';
  }

  /**
   * Check if task failed
   */
  isTaskFailed(task: UploadTask): boolean {
    return task.status === 'failed';
  }

  /**
   * Check if task is completed
   */
  isTaskCompleted(task: UploadTask): boolean {
    return task.status === 'completed';
  }
}
